import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { exists } from '@jack-henry/frontend-utils/functions';
import { AppType, ConfigurationService } from '@treasury/core/config';
import { BoAccountService } from '@treasury/domain/backoffice/services';
import { ChannelAuthenticationService } from '@treasury/domain/channel/services/account/authentication-service';
import { StatusCode } from '@treasury/domain/channel/services/login';
import { NotificationService, TmBaseComponent } from '@treasury/presentation';
import { ButtonConfig } from '@treasury/presentation/components/tm-footer.types';
import { debounce } from '@treasury/utils';
import { DialogOpenedChangedEvent } from '@vaadin/dialog';
import { dialogFooterRenderer, dialogRenderer } from '@vaadin/dialog/lit.js';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LoginClient } from '../login-client';

import '@treasury/omega/components/progress/omega-progress.js';
import '@treasury/presentation/components/tm-button';
import '@treasury/presentation/components/tm-footer';
import '@treasury/presentation/components/tm-password-field';
// eslint-disable-next-line no-duplicate-imports
import '@vaadin/dialog';

export const tagName = 'change-password';
@customElement(tagName)
export default class ChangePassword extends TmBaseComponent {
    @property({ type: Boolean })
    public loading = false;

    @property({ type: Number })
    public daysUntilPasswordExpires?: number;

    @property({ type: String })
    public institutionId = '';

    @InjectProperty()
    declare private readonly config: ConfigurationService;

    @InjectProperty()
    declare private notificationService: NotificationService;

    @state()
    private verifiedUser = {};

    @state()
    private requirements: string[] = [];

    @state()
    private statusCode = StatusCode.Unknown;

    @state()
    private password = '';

    @state()
    private confirmPassword = '';

    @state()
    private passwordRequirementsDialogOpened = false;

    @state()
    private debouncedPasswordsMatch = true;

    @InjectProperty()
    declare private readonly client: LoginClient;

    @InjectProperty()
    declare private channelAuthService: ChannelAuthenticationService;

    @InjectProperty()
    declare private readonly boAccountService: BoAccountService;

    private get isConsumer() {
        return this.config.app !== AppType.BackOffice;
    }

    private debouncePasswordMismatchWarning = debounce(
        () => (this.debouncedPasswordsMatch = this.passwordsMatch),
        500
    );

    private get passwordsMatch() {
        return this.password === this.confirmPassword;
    }

    private get passwordIsExpired() {
        return (
            this.statusCode === StatusCode.ChallengeResetExpiredPassword ||
            this.statusCode === StatusCode.ResetExpiredPassword
        );
    }

    private get hasPasswordRequirements() {
        return this.requirements && this.requirements.length > 0;
    }

    private get passwordNotExpiring() {
        return !this.passwordIsExpired && !exists(this.daysUntilPasswordExpires);
    }

    private get buttonConfig() {
        const config: ButtonConfig[] = [
            {
                text: 'Submit',
                onClick: () => this.submitPassword(),
                disabled: !this.confirmPassword || !this.passwordsMatch,
            },
        ];

        if (this.daysUntilPasswordExpires && this.daysUntilPasswordExpires > 0) {
            config.push({
                importance: 'tertiary',
                text: 'Change Password Later',
                onClick: () => this.changePasswordLater(),
            });
        }
        return config;
    }

    protected render() {
        if (this.loading) {
            return html`<omega-progress card></omega-progress>`;
        }
        return html`
            ${this.renderPasswordRequirementsDialog()}
            <div class="m-4">
                <section class="pb-2">
                    <p>
                        Please enter a new
                        password${this.hasPasswordRequirements
                            ? html` following the
                                  <span
                                      class="underline text-[color:var(--brand-color)]"
                                      @click=${(e: Event) => {
                                          e.preventDefault();
                                          this.passwordRequirementsDialogOpened = true;
                                      }}
                                  >
                                      password requirements </span
                                  >.`
                            : '.'}
                    </p>
                    ${this.renderPasswordExpiredMessage()}
                    ${this.renderPasswordWillExpireSoonMessage()}
                </section>
                <tm-password-field
                    label="Password"
                    .initial=${this.password}
                    @value-changed=${(e: CustomEvent) => {
                        this.password = e.detail.value;
                        this.debouncedPasswordsMatch = true;
                        this.debouncePasswordMismatchWarning();
                    }}
                    @keydown=${this.onKeyDown}
                    required
                    class="pb-1"
                >
                </tm-password-field>
                <tm-password-field
                    label="Confirm Password"
                    .initial=${this.confirmPassword}
                    @value-changed=${(e: CustomEvent) => {
                        this.confirmPassword = e.detail.value;
                        this.debouncedPasswordsMatch = true;
                        this.debouncePasswordMismatchWarning();
                    }}
                    @keydown=${this.onKeyDown}
                    required
                    class="pb-1"
                >
                </tm-password-field>
                ${this.renderPasswordMismatchWarning()}
            </div>
            <tm-footer .buttonConfig=${this.buttonConfig}></tm-footer>
        `;
    }

    protected async firstUpdated() {
        try {
            this.loading = true;
            if (this.isConsumer) {
                this.requirements = await this.client.passwordRequirements(this.institutionId);
            } else {
                this.requirements = await this.boAccountService.getPasswordRequirements(
                    this.institutionId
                );
            }
        } catch (err) {
            this.notificationService.renderError(
                err instanceof Error ? err : 'An unknown error occurred. Please try again.'
            );
        } finally {
            this.loading = false;
        }
    }

    private async submitPassword() {
        this.dispatchEvent(new CustomEvent('submit', { detail: this.password }));
    }

    private onKeyDown(e: KeyboardEvent) {
        if (e.code === 'Enter') {
            this.submitPassword();
        }
    }

    private async changePasswordLater() {
        if (this.config.app === AppType.BackOffice) {
            throw new Error('Not implemented in backoffice yet.');
            return;
        }
        await this.channelAuthService.goToNextStep();
    }

    private renderPasswordRequirementsDialog() {
        if (!this.hasPasswordRequirements) {
            return nothing;
        }
        return html`<vaadin-dialog
            id="password-requirements-dialog"
            header-title="Password Requirements"
            .opened=${this.passwordRequirementsDialogOpened}
            @opened-changed=${(event: DialogOpenedChangedEvent) => {
                this.passwordRequirementsDialogOpened = event.detail.value;
            }}
            ${dialogRenderer(this.renderPasswordRequirements)}
            ${dialogFooterRenderer(this.renderPasswordRequirementsDialogFooter)}
        ></vaadin-dialog>`;
    }

    private renderPasswordRequirementsDialogFooter() {
        return html`<div class="w-full flex justify-center">
            <tm-button
                importance="primary"
                @click=${() => {
                    this.passwordRequirementsDialogOpened = false;
                }}
            >
                Close
            </tm-button>
        </div>`;
    }

    private renderPasswordExpiredMessage() {
        if (!this.passwordIsExpired) {
            return nothing;
        }
        return html` <span> Your password has expired. </span>`;
    }

    private renderPasswordWillExpireSoonMessage() {
        if (this.passwordNotExpiring || !this.daysUntilPasswordExpires) {
            return nothing;
        }
        return html`
            <span> Your password will expire in ${this.daysUntilPasswordExpires} days. </span>
        `;
    }

    private renderPasswordRequirements() {
        const requirements = this.requirements.map(
            req => html`<li class="text-sm pb-1">${req}</li>`
        );
        return html`
            <ul>
                ${requirements}
            </ul>
        `;
    }

    private renderPasswordMismatchWarning() {
        if (this.confirmPassword.length === 0 || this.debouncedPasswordsMatch) {
            return nothing;
        }
        return html` <span class="text-sm" style="color:red"> Passwords Do Not Match </span>`;
    }

    public static get styles() {
        return [
            css`
                :host() {
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                }

                :host([stickyheader]) {
                    display: block !important;
                }

                :host([stickyheader]) #content {
                    padding-bottom: var(--sticky-header-height, 0);
                }

                tm-footer {
                    padding-top: 1em;
                }

                @media only screen and (min-width: 640px) {
                    :host([stickyheader]) #content {
                        padding-bottom: 0px;
                    }
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: ChangePassword;
    }
}
